import React, { FormEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCollections } from '../hooks/useCollections';
import { useRepositories } from '../hooks/useRepositories';
import type { Collection } from '../domain/collections';
import { ADD_A_TITLE } from '../domain/listings';
import type { ListingType, PendingPhoto, PublishChoice } from '../domain/listings';
import { LISTING_TYPES, PUBLISH_SUBMIT_LABEL } from '../utils/listings';
import ImageUpload from '../components/ImageUpload';
import PublishChoiceField from '../components/PublishChoiceField';
import { ActionError, LoadingState, StateForError } from '../components/PageStates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

/**
 * Post a listing: publish now, save a draft, or schedule it (016 FR-001–FR-003).
 * Only a draft may be saved without a title (021 FR-010).
 * Opened from one of my collections it starts there; otherwise on my default.
 */
export default function AddListing() {
  const collections = useCollections();

  if (collections.status === 'loading') return <LoadingState label="Loading your collections" />;
  if (collections.status === 'error') {
    return <StateForError error={collections.error} onRetry={collections.reload} />;
  }
  return <ListingForm mine={collections.data.filter((c) => c.myRelation === 'owner')} />;
}

function ListingForm({ mine }: { mine: Collection[] }) {
  const navigate = useNavigate();
  const { listings } = useRepositories();
  const { collectionId } = useParams<{ collectionId?: string }>();
  const startingCollection =
    mine.find((c) => c.id === collectionId) ?? mine.find((c) => c.isDefault);

  const [collection, setCollection] = useState(startingCollection?.id ?? '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [listingType, setListingType] = useState<ListingType>('other');
  const [price, setPrice] = useState('');
  const [location, setLocation] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [pendingImages, setPendingImages] = useState<PendingPhoto[]>([]);
  const [publish, setPublish] = useState<PublishChoice>('now');
  const [publishAt, setPublishAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!collection) {
      setError(new Error('Choose a collection.'));
      return;
    }
    if (publish !== 'draft' && !title.trim()) {
      setError(new Error(ADD_A_TITLE));
      return;
    }
    if (publish === 'scheduled' && !publishAt) {
      setError(new Error('Choose when to publish it.'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await listings.create({
        collectionId: collection,
        title: title.trim(),
        description: description.trim(),
        listingType,
        price: price ? price : null,
        location: location.trim(),
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        publish,
        publishAt: publish === 'scheduled' ? publishAt : null,
        pendingPhotoIds: pendingImages.map((img) => img.id),
      });
      navigate(`/listings/${created.id}`);
    } catch (err) {
      setError(err);
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-8 text-4xl font-bold tracking-tight text-foreground">New listing</h1>

      <section className="mb-6 rounded-3xl bg-card p-6">
        <h2 className="mb-2 text-xl font-semibold text-foreground">Photos</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          They upload now and are added when you publish.
        </p>
        <ImageUpload
          maxImages={20}
          maxFileSize={20 * 1024 * 1024}
          onImagesChange={setPendingImages}
          autoUpload
        />
      </section>

      <form onSubmit={submit} className="space-y-6 rounded-3xl bg-card p-6">
        {error !== null && <ActionError error={error} />}

        <div>
          <Label id="collection-label">Collection</Label>
          <Select value={collection} onValueChange={setCollection}>
            <SelectTrigger aria-labelledby="collection-label" className="mt-1">
              <SelectValue placeholder="Choose a collection" />
            </SelectTrigger>
            <SelectContent>
              {mine.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="What is it?"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="description">Description (optional)</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="Condition, size, the story behind it"
            className="mt-1"
          />
        </div>

        <div>
          <Label id="listing-type-label">Type</Label>
          <Select value={listingType} onValueChange={(v) => setListingType(v as ListingType)}>
            <SelectTrigger aria-labelledby="listing-type-label" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LISTING_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <Label htmlFor="price">Price (optional)</Label>
            <InputGroup className="mt-1">
              <InputGroupAddon aria-hidden="true">$</InputGroupAddon>
              <InputGroupInput
                type="number"
                id="price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                step="0.01"
                min="0"
                placeholder="0.00"
                className="[-moz-appearance:textfield] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
            </InputGroup>
          </div>
          <div>
            <Label htmlFor="location">Location (optional)</Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              maxLength={100}
              placeholder="Neighborhood or city"
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="expires_at">Expires (optional)</Label>
          <Input
            type="datetime-local"
            id="expires_at"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="mt-1"
          />
        </div>

        <PublishChoiceField
          publish={publish}
          onPublishChange={setPublish}
          publishAt={publishAt}
          onPublishAtChange={setPublishAt}
        />

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            className="rounded-full text-primary"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="submit" className="rounded-full" disabled={saving}>
            {saving ? 'Saving…' : PUBLISH_SUBMIT_LABEL[publish]}
          </Button>
        </div>
      </form>
    </div>
  );
}
